import SalesDataOrder from 'mage-swagfaces/sales/SalesDataOrder';
import SalesDataOrderAddress from 'mage-swagfaces/sales/SalesDataOrderAddress';
import SalesDataOrderExtension from 'mage-swagfaces/sales/SalesDataOrderExtension';
import { AUOrder } from '../../types/magento';
import { format, isValid, parseISO } from 'date-fns';
import {
  GIFT_CARD_SKU,
  GRACE_PERIOD_STATUSES,
  STATUS_CANCELED,
  STATUS_HOLDED,
  STATUS_LABEL_CANCELED,
  STATUS_LABEL_ON_HOLD,
  STATUS_LABEL_ORDER_RECEIVED,
  STATUS_LABEL_PROCESSING,
  STATUS_LABEL_SHIPPED,
  STATUS_RECEIVED,
  STATUS_SHIPPED
} from 'constants/orders';

interface OrderExtensionAttributes extends SalesDataOrderExtension {
  status_label: string;
}

export const getFormattedOrderDate = (createdAt: string | Date): string => {
  if (typeof createdAt !== 'string') {
    return '';
  }

  const parsedDate = Date.parse(createdAt.replace(/[-]/g, '/'));
  if (isNaN(parsedDate)) {
    return '';
  }

  return new Date(parsedDate).toLocaleDateString('en-us', { year: 'numeric', month: 'long', day: 'numeric' });
};

//a little hacky, but helps preserve the template literals without having to jam the text against the left side of the screen
export const removeLeadingWhiteSpaces = (str: string): string =>
  str
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean)
    .join('\n');

export const getFormattedAddress = (address: SalesDataOrderAddress): string => {
  const { firstname, lastname, street, city, region, postcode, country_id, telephone } = address;

  if (!street?.length) {
    return '';
  }

  const formattedAddress = `
    ${firstname} ${lastname}
    ${street[0] || ''} ${street[1] || ''}
    ${city}, ${region}, ${postcode},
    ${country_id}
    T: ${telephone}
  `;

  return removeLeadingWhiteSpaces(formattedAddress);
};

export const getOrderStatusLabel = (order: SalesDataOrder): string => {
  const extensionAttributes = order.extension_attributes as OrderExtensionAttributes;
  const statusLabel = extensionAttributes.status_label || 'Processing';

  return statusLabel;
};

//This is a temporary solution to show the order status' in readable form until the above getOrderStatusLabel is fixed in Magento.
//Currently, the status_label does not exist on all orders returned from Magento.
const readableStatusLabels = [
  {orderStatus: STATUS_HOLDED, label: STATUS_LABEL_ON_HOLD},
  {orderStatus: STATUS_SHIPPED, label: STATUS_LABEL_SHIPPED},
  {orderStatus: STATUS_CANCELED, label: STATUS_LABEL_CANCELED},
  {orderStatus: STATUS_RECEIVED, label: STATUS_LABEL_ORDER_RECEIVED}
];
export const getOrderDetailsStatus = (order: SalesDataOrder): string => {
  return readableStatusLabels.find(statusLabel =>
    order.status.includes(statusLabel.orderStatus))?.label || STATUS_LABEL_PROCESSING;
};

export const isWaitingForGracePeriod = (order: SalesDataOrder): boolean => {
  return GRACE_PERIOD_STATUSES.includes(order?.status);
};

export const orderContainsGiftCard = (order: SalesDataOrder): boolean => {
  return order.items.filter(item => item.sku === GIFT_CARD_SKU).length > 0;
};
export const getFormattedOrderShippingAddress = (order: SalesDataOrder): string => {
  const shipping_assignment = order?.extension_attributes?.shipping_assignments;
  const address = shipping_assignment ? shipping_assignment[0]?.shipping?.address : null;

  if (!address) {
    return '';
  }

  return getFormattedAddress(address);
};

export const getFormattedOrderBillingAddress = (order: SalesDataOrder): string => {
  const address = order?.billing_address;
  if (!address) {
    return '';
  }

  return getFormattedAddress(address);
};

export const getFormattedPaymentInformation = (order: SalesDataOrder): string => {
  let formattedPayment;

  const paymentInfo = order?.payment?.additional_information;
  let name, ccTypeOrPPName, last4;

  if (paymentInfo?.length) {
    name = paymentInfo[0] || '';
    ccTypeOrPPName = paymentInfo[8] || '';
    last4 = paymentInfo[9] || '';
  }

  switch (order?.payment?.method) {
    case 'free':
      formattedPayment = 'No Payment Information Required';
      break;
    case 'braintree':
      formattedPayment = `
        Credit Card
        ${name}
        ${ccTypeOrPPName}
        ${last4}
      `;
      break;
    case 'braintree_paypal':
      formattedPayment = `
        PayPal
        ${ccTypeOrPPName}
      `;
      break;
    case 'afterpay':
      formattedPayment = 'Afterpay';
      break;
    default:
      formattedPayment = 'No Payment Information Found';
  }

  return removeLeadingWhiteSpaces(formattedPayment);
};

export const parseOrderIds = (items: SalesDataOrder[]): string[] => {
  return items.map(order => order.increment_id);
};


export const getOrderETA = (order: AUOrder | null): string | null => {
  const extensions = order?.items?.[0]?.extension_attributes;
  const min = extensions?.min_arrival_date;
  const max = extensions?.max_arrival_date;

  if (!min || !max) {
    return null;
  }

  const minDate = parseISO(min);
  const maxDate = parseISO(max);

  if (!isValid(minDate) || !isValid(maxDate)) {
    return null;
  }

  const minFormatted = format(minDate, 'EEEE, MMMM d, yyyy');
  const maxFormatted = format(maxDate, 'EEEE, MMMM d, yyyy');

  return minFormatted === maxFormatted ? minFormatted : `${minFormatted} - ${maxFormatted}`;
};
